import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import logo from 'assets/images/logo.png';
import searchImg from 'assets/images/search.png';
import alertImg from 'assets/images/alert_rank.png';
import chartImg from 'assets/images/chart_rank.png';
import dataImg from 'assets/images/data.png';
import dashboardPreview from 'assets/images/dashboard_preview.png';
import { menu, KAKAO_OPEN_CHAT } from 'utils/constants';

const FEATURES = [
  {
    icon: searchImg,
    title: '전국 급매 한눈에',
    description:
      '전국 아파트 단지의 실시간 매물과 시세를 수집·분석해 진짜 급매만 골라드립니다.',
  },
  {
    icon: alertImg,
    title: '조건별 급매 알림',
    description:
      '원하는 지역·평형·하락률 조건을 설정하면 새로운 급매가 나올 때마다 이메일로 알려드립니다.',
  },
  {
    icon: chartImg,
    title: '갭메우기 분석',
    description:
      '단지별 가격 흐름과 갭메우기 가능성을 분석해 투자 타이밍을 도와드립니다.',
  },
  {
    icon: dataImg,
    title: '매일 업데이트되는 데이터',
    description:
      '네이버 부동산 매물을 매일 크롤링해 최신 시세·거래·전고점 정보를 제공합니다.',
  },
];

const STEPS = [
  {
    step: '01',
    title: '지역과 조건을 선택하세요',
    description:
      '관심 있는 시·구·평형·세대수·하락률 등 조건을 선택해 급매를 필터링합니다.',
  },
  {
    step: '02',
    title: '급매 매물을 한눈에 확인',
    description:
      '전고점 대비 하락률, 시세 추이, 단지 정보까지 한 화면에서 비교합니다.',
  },
  {
    step: '03',
    title: '새로운 급매를 메일로',
    description:
      '알림을 켜두면 조건에 맞는 새 급매가 올라올 때마다 메일로 받아볼 수 있습니다.',
  },
];

const PLAN_TEASERS = [
  {
    key: 'free',
    name: 'FREE',
    price: '0원',
    highlight: '서울시 강남구 체험',
    color: '#a3aed0',
  },
  {
    key: 'basic',
    name: 'BASIC',
    price: '9,900원/월',
    highlight: '전국 조회 + 주 1회 급매 알림',
    color: '#644ded',
  },
  {
    key: 'premium',
    name: 'PREMIUM',
    price: '19,900원/월',
    highlight: '갭메우기 분석 + 주 3회 급매 알림',
    color: '#ff6b35',
  },
];

const Landing = () => {
  const navigate = useNavigate();

  const goLogin = () => navigate(menu.login.link);
  const goPricing = () => navigate(menu.pricing.link);

  const primaryLabel = '로그인 / 회원가입';

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Page>
      <Gnb>
        <GnbInner>
          <GnbBrand onClick={() => scrollTo('top')}>
            <GnbLogo src={logo} alt="급매 탐색기" />
            <GnbName>급매 탐색기</GnbName>
          </GnbBrand>
          <GnbMenus>
            <GnbLink onClick={() => scrollTo('features')}>기능</GnbLink>
            <GnbLink onClick={() => scrollTo('how')}>사용법</GnbLink>
            <GnbLink onClick={() => scrollTo('pricing')}>요금제</GnbLink>
            <GnbExternalLink
              href={KAKAO_OPEN_CHAT.link}
              target="_blank"
              rel="noreferrer"
            >
              {KAKAO_OPEN_CHAT.name}
            </GnbExternalLink>
          </GnbMenus>
          <GnbActions>
            <GnbPrimaryButton onClick={goLogin}>
              {primaryLabel}
            </GnbPrimaryButton>
          </GnbActions>
        </GnbInner>
      </Gnb>
      <Hero id="top">
        <HeroInner>
          <BrandRow>
            <Logo src={logo} alt="급매 탐색기" />
            <BrandName>급매 탐색기</BrandName>
          </BrandRow>
          <Headline>
            시세보다 싸게 나온 아파트,
            <br />
            대신 찾아드립니다.
          </Headline>
          <SubHeadline>
            전국 네이버 부동산 데이터를 매일 수집·분석해
            <br />
            진짜 급매 매물과 갭메우기 기회를 알려드립니다.
          </SubHeadline>
          <CtaRow>
            <PrimaryButton onClick={goLogin}>{primaryLabel}</PrimaryButton>
            <SecondaryButton onClick={goPricing}>요금제 보기</SecondaryButton>
          </CtaRow>
          <PreviewFrame>
            <PreviewShadow />
            <PreviewImage
              src={dashboardPreview}
              alt="급매 탐색기 부동산 매물 분석 현황"
            />
          </PreviewFrame>
        </HeroInner>
      </Hero>

      <Section id="features">
        <SectionEyebrow>FEATURES</SectionEyebrow>
        <SectionTitle>급매 탐색기로 할 수 있는 일</SectionTitle>
        <SectionSub>
          지금 당장 써먹을 수 있는 4가지 핵심 기능을 제공합니다.
        </SectionSub>
        <FeatureGrid>
          {FEATURES.map((f) => (
            <FeatureCard key={f.title}>
              <FeatureIcon src={f.icon} alt="" />
              <FeatureTitle>{f.title}</FeatureTitle>
              <FeatureDesc>{f.description}</FeatureDesc>
            </FeatureCard>
          ))}
        </FeatureGrid>
      </Section>

      <ShowcaseSection>
        <ShowcaseInner>
          <ShowcaseText>
            <SectionEyebrow>PRODUCT</SectionEyebrow>
            <ShowcaseTitle>
              전고점 대비 하락률부터
              <br />
              시세 추이까지, 한 화면에.
            </ShowcaseTitle>
            <ShowcaseDesc>
              평형별 거래가, 전세가, 매물가의 흐름을 스파크라인으로 비교하고
              하락률·갭률을 한 번에 확인할 수 있습니다. 관심 단지를 빠르게
              스크리닝하고, 우선순위를 세워 조사 시간을 줄이세요.
            </ShowcaseDesc>
            <BulletList>
              <Bullet>전국 12,000개+ 단지 분석</Bullet>
              <Bullet>평형별 시세·전세·매물가 스파크라인</Bullet>
              <Bullet>하락률, 갭률, 급매 여부 자동 계산</Bullet>
            </BulletList>
            <CtaRow style={{ justifyContent: 'flex-start', marginTop: 28 }}>
              <PrimaryButton onClick={goLogin}>{primaryLabel}</PrimaryButton>
            </CtaRow>
          </ShowcaseText>
          <ShowcaseImageWrap>
            <ShowcaseImage
              src={dashboardPreview}
              alt="급매 탐색기 매물 분석 테이블"
            />
          </ShowcaseImageWrap>
        </ShowcaseInner>
      </ShowcaseSection>

      <Section id="how">
        <SectionEyebrow>HOW IT WORKS</SectionEyebrow>
        <SectionTitle>3단계로 급매를 찾아보세요</SectionTitle>
        <StepGrid>
          {STEPS.map((s) => (
            <StepCard key={s.step}>
              <StepNumber>{s.step}</StepNumber>
              <StepTitle>{s.title}</StepTitle>
              <StepDesc>{s.description}</StepDesc>
            </StepCard>
          ))}
        </StepGrid>
      </Section>

      <Section id="pricing">
        <SectionEyebrow>PRICING</SectionEyebrow>
        <SectionTitle>필요한 만큼만, 부담 없는 요금제</SectionTitle>
        <SectionSub>
          무료 체험으로 먼저 써보고 필요할 때 업그레이드하세요.
        </SectionSub>
        <PlanTeaserGrid>
          {PLAN_TEASERS.map((p) => (
            <PlanTeaserCard key={p.key} onClick={goPricing}>
              <PlanTeaserBadge color={p.color}>{p.name}</PlanTeaserBadge>
              <PlanTeaserPrice>{p.price}</PlanTeaserPrice>
              <PlanTeaserHighlight>{p.highlight}</PlanTeaserHighlight>
            </PlanTeaserCard>
          ))}
        </PlanTeaserGrid>
        <CtaRow style={{ marginTop: 32 }}>
          <SecondaryButton onClick={goPricing}>
            요금제 자세히 보기
          </SecondaryButton>
        </CtaRow>
      </Section>

      <FinalCta>
        <FinalCtaTitle>지금 바로 내 집 마련의 기회를 찾아보세요</FinalCtaTitle>
        <FinalCtaSub>
          무료 플랜으로 서울시 강남구 급매부터 확인할 수 있습니다.
        </FinalCtaSub>
        <CtaRow>
          <PrimaryButton onClick={goLogin}>{primaryLabel}</PrimaryButton>
          <SecondaryButton onClick={goPricing}>요금제 보기</SecondaryButton>
        </CtaRow>
      </FinalCta>
    </Page>
  );
};

const Page = styled.div`
  width: 100%;
  background-color: #fff;
`;

const Gnb = styled.header`
  position: sticky;
  top: 0;
  z-index: 100;
  background-color: rgba(255, 255, 255, 0.92);
  backdrop-filter: saturate(180%) blur(8px);
  border-bottom: 1px solid #eee;
`;

const GnbInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  height: 64px;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
`;

const GnbBrand = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const GnbLogo = styled.img`
  width: 30px;
  height: 30px;
  object-fit: contain;
`;

const GnbName = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #232d42;
  letter-spacing: -0.02rem;
`;

const GnbMenus = styled.nav`
  display: flex;
  align-items: center;
  gap: 4px;

  @media (max-width: 720px) {
    display: none;
  }
`;

const gnbItemStyles = `
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: 1;
  color: #3a4150;
  background: none;
  border: none;
  border-radius: 8px;
  text-decoration: none;
  letter-spacing: -0.02rem;
  cursor: pointer;
  box-sizing: border-box;
`;

const GnbLink = styled.button`
  ${gnbItemStyles}

  &:hover {
    background-color: #f5f3ff;
    color: #8800ff;
  }
`;

const GnbExternalLink = styled.a`
  ${gnbItemStyles}

  &::before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    background-color: #fbe300;
    border-radius: 50%;
  }

  &:hover {
    background-color: #fffbe0;
    color: #8800ff;
  }
`;

const GnbActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const GnbPrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  height: 36px;
  padding: 0 18px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  color: #fff;
  background-color: #8800ff;
  border: none;
  border-radius: 8px;
  letter-spacing: -0.02rem;
  cursor: pointer;
  box-sizing: border-box;

  &:hover {
    background-color: #7000d9;
  }
`;

const Hero = styled.section`
  background: linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%);
  padding: 96px 24px 80px;
`;

const HeroInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  text-align: center;
`;

const BrandRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 28px;
`;

const Logo = styled.img`
  width: 56px;
  height: 56px;
  object-fit: contain;
`;

const BrandName = styled.div`
  font-size: 22px;
  font-weight: 600;
  color: #232d42;
  letter-spacing: -0.03rem;
`;

const Headline = styled.h1`
  font-size: 44px;
  font-weight: 700;
  line-height: 1.3;
  color: #232d42;
  letter-spacing: -0.04rem;
  margin: 0 0 20px;
`;

const SubHeadline = styled.p`
  font-size: 17px;
  line-height: 1.6;
  color: #5e646d;
  margin: 0 0 40px;
  letter-spacing: -0.02rem;
`;

const CtaRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.button`
  padding: 14px 32px;
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  background-color: #8800ff;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 18px rgba(136, 0, 255, 0.25);
  }
`;

const SecondaryButton = styled.button`
  padding: 14px 32px;
  font-size: 15px;
  font-weight: 600;
  color: #8800ff;
  background-color: #fff;
  border: 1px solid #d8c8ff;
  border-radius: 10px;
  cursor: pointer;
  transition: transform 0.15s ease, background-color 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    background-color: #faf7ff;
  }
`;

const PreviewFrame = styled.div`
  position: relative;
  margin: 64px auto 0;
  max-width: 1000px;
`;

const PreviewShadow = styled.div`
  position: absolute;
  inset: 20px 40px -20px 40px;
  background: radial-gradient(
    ellipse at center,
    rgba(136, 0, 255, 0.25) 0%,
    rgba(136, 0, 255, 0) 70%
  );
  filter: blur(30px);
  z-index: 0;
`;

const PreviewImage = styled.img`
  position: relative;
  z-index: 1;
  width: 100%;
  border-radius: 14px;
  border: 1px solid #e4dcff;
  box-shadow: 0 20px 50px rgba(35, 45, 66, 0.15);
  background-color: #fff;
`;

const Section = styled.section`
  max-width: 1100px;
  margin: 0 auto;
  padding: 80px 24px;
  text-align: center;
  scroll-margin-top: 80px;
`;

const SectionEyebrow = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #8800ff;
  letter-spacing: 0.15em;
  margin-bottom: 12px;
`;

const SectionTitle = styled.h2`
  font-size: 30px;
  font-weight: 700;
  color: #232d42;
  margin: 0 0 12px;
  letter-spacing: -0.03rem;
`;

const SectionSub = styled.p`
  font-size: 15px;
  color: #5e646d;
  margin: 0 0 48px;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;

  @media (max-width: 1020px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background-color: #fff;
  border: 1px solid #eee;
  border-radius: 16px;
  padding: 32px 22px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.08);
  }
`;

const FeatureIcon = styled.img`
  width: 44px;
  height: 44px;
  object-fit: contain;
  margin-bottom: 16px;
`;

const FeatureTitle = styled.h3`
  font-size: 17px;
  font-weight: 700;
  color: #232d42;
  margin: 0 0 10px;
  letter-spacing: -0.02rem;
`;

const FeatureDesc = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: #5e646d;
  margin: 0;
  letter-spacing: -0.02rem;
`;

const ShowcaseSection = styled.section`
  background-color: #fafaff;
  padding: 80px 24px;
`;

const ShowcaseInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 56px;
  align-items: center;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ShowcaseText = styled.div`
  text-align: left;
`;

const ShowcaseTitle = styled.h2`
  font-size: 30px;
  font-weight: 700;
  color: #232d42;
  margin: 0 0 16px;
  line-height: 1.3;
  letter-spacing: -0.03rem;
`;

const ShowcaseDesc = styled.p`
  font-size: 15px;
  line-height: 1.7;
  color: #5e646d;
  margin: 0 0 24px;
`;

const BulletList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const Bullet = styled.li`
  position: relative;
  padding: 6px 0 6px 22px;
  font-size: 14px;
  color: #3a4150;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 13px;
    width: 8px;
    height: 8px;
    background-color: #8800ff;
    border-radius: 50%;
  }
`;

const ShowcaseImageWrap = styled.div`
  position: relative;
`;

const ShowcaseImage = styled.img`
  width: 100%;
  border-radius: 14px;
  border: 1px solid #e4dcff;
  box-shadow: 0 16px 40px rgba(35, 45, 66, 0.12);
  background-color: #fff;
`;

const StepGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const StepCard = styled.div`
  background-color: #fff;
  border: 1px solid #eee;
  border-radius: 16px;
  padding: 32px 24px;
  text-align: left;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`;

const StepNumber = styled.div`
  font-size: 32px;
  font-weight: 800;
  color: #8800ff;
  letter-spacing: -0.04rem;
  margin-bottom: 12px;
`;

const StepTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #232d42;
  margin: 0 0 10px;
  letter-spacing: -0.02rem;
`;

const StepDesc = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: #5e646d;
  margin: 0;
`;

const PlanTeaserGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const PlanTeaserCard = styled.div`
  background-color: #fff;
  border: 1px solid #eee;
  border-radius: 16px;
  padding: 28px 24px;
  text-align: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.08);
  }
`;

const PlanTeaserBadge = styled.div`
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 14px;
  border-radius: 20px;
  color: #fff;
  background-color: ${({ color }) => color};
  margin-bottom: 14px;
  letter-spacing: 0.08em;
`;

const PlanTeaserPrice = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: #232d42;
  margin-bottom: 6px;
`;

const PlanTeaserHighlight = styled.div`
  font-size: 13px;
  color: #5e646d;
  line-height: 1.5;
`;

const FinalCta = styled.section`
  background: linear-gradient(135deg, #f8f7ff 0%, #f0eaff 100%);
  padding: 80px 24px;
  text-align: center;
`;

const FinalCtaTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #232d42;
  margin: 0 0 12px;
  letter-spacing: -0.03rem;
`;

const FinalCtaSub = styled.p`
  font-size: 15px;
  color: #5e646d;
  margin: 0 0 28px;
`;

export default Landing;
